import React, { useEffect, useMemo, useRef, useState } from 'react';
import TopBar from '@/components/TopBar';
import { translate, getTranslateLanguages } from '@/lib/api';
import { ArrowLeft, Copy, Check, Loader2, Wand2, Mic, MicOff, Volume2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const QUICK = [
  'Where is the nearest hospital?',
  'How much does this cost?',
  'Can you help me?',
  'Where is the bathroom?',
  'I need a taxi.',
  "I'm allergic to peanuts.",
];

// Pick a sensible default target language by user's browser locale
function defaultTargetCode() {
  const nav = (navigator.language || 'en').split('-')[0];
  if (nav === 'en') return 'es';
  return 'en';
}

export default function Translate() {
  const navigate = useNavigate();
  const [text, setText] = useState('Where is the nearest hospital?');
  const [languages, setLanguages] = useState([]);
  const [target, setTarget] = useState(defaultTargetCode());
  const [result, setResult] = useState('');
  const [engine, setEngine] = useState(null);
  const [detectedSource, setDetectedSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  // Load supported languages
  useEffect(() => {
    let alive = true;
    getTranslateLanguages()
      .then((langs) => { if (alive) setLanguages(langs || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Setup speech recognition once
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-US';
    rec.onresult = (e) => {
      const transcript = Array.from(e.results).map((r) => r[0].transcript).join('');
      setText(transcript);
      setListening(false);
      setTimeout(() => run(transcript), 50);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
  }, []); // eslint-disable-line

  const targetName = useMemo(() => (languages.find((l) => l.code === target) || {}).name || target, [languages, target]);

  const run = async (t = text) => {
    if (!t.trim()) { toast.error('Enter some text'); return; }
    setLoading(true); setResult(''); setEngine(null); setDetectedSource(null);
    try {
      const r = await translate(t, target);
      setResult(r.translation || '');
      setEngine(r.engine || null);
      setDetectedSource(r.detected_source || null);
    } catch (e) {
      toast.error('Translation failed');
    } finally { setLoading(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
  };

  const speak = () => {
    if (!result || !('speechSynthesis' in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(result);
      utt.lang = target;
      utt.rate = 0.95;
      window.speechSynthesis.speak(utt);
    } catch {}
  };

  const startListening = () => {
    const rec = recognitionRef.current;
    if (!rec) { toast.error('Voice input not supported on this device'); return; }
    try { rec.start(); setListening(true); } catch (e) { setListening(false); }
  };
  const stopListening = () => {
    const rec = recognitionRef.current;
    try { rec && rec.stop(); } catch {}
    setListening(false);
  };

  const detectedName = useMemo(() => {
    if (!detectedSource) return null;
    const l = languages.find((x) => x.code === detectedSource || x.code.startsWith(detectedSource));
    return l?.name || detectedSource;
  }, [detectedSource, languages]);

  return (
    <div data-testid="translate-screen">
      <TopBar />
      <div className="px-5 pt-1 flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#0F1A13]/05 border border-black/5 flex items-center justify-center hover:bg-[#C4FF3D]/30 puvio-focus" aria-label="Back" data-testid="translate-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Translate</p>
          <h1 className="text-2xl font-semibold tracking-tight">Any phrase, any language</h1>
        </div>
      </div>

      <div className="px-5 pt-4 flex flex-col gap-4">
        <div className="puvio-card p-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-[#4A5A50]">Source text</label>
            {detectedName && !loading && result && (
              <span className="text-[11px] text-[#4A5A50] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#15291C]" /> Detected: {detectedName}
              </span>
            )}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full min-h-[96px] rounded-2xl border border-black/10 bg-white p-3 text-sm puvio-focus resize-none"
            data-testid="translate-source-input"
            placeholder="Type, paste, or tap the mic..."
          />
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={listening ? stopListening : startListening}
              className={`h-10 w-10 rounded-full flex items-center justify-center puvio-focus border transition-all ${listening ? 'bg-[#FF4D4F] text-[#0F1A13] border-transparent animate-pulse' : 'bg-white border-black/10 hover:bg-[#0F1A13]/05'}`}
              aria-label={listening ? 'Stop voice input' : 'Start voice input'}
              data-testid="translate-mic-button"
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-full bg-[#0F1A13]/05 border border-black/10 px-3 text-sm puvio-focus min-w-[140px]" data-testid="translate-target-select" aria-label="Target language">
              {(languages.length ? languages : [{ code: 'en', name: 'English' }, { code: 'fr', name: 'French' }, { code: 'es', name: 'Spanish' }]).map((l) => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
            <button type="button" onClick={() => run()} disabled={loading} className="ml-auto h-11 px-5 rounded-full bg-[#C4FF3D] text-[#0F1A13] font-semibold text-sm flex items-center gap-2 disabled:opacity-70 puvio-focus" data-testid="translate-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              <span>{loading ? 'Translating…' : 'Translate'}</span>
            </button>
          </div>
        </div>

        <div className="puvio-card p-4 min-h-[140px]" data-testid="translate-output-card">
          <div className="flex items-start justify-between gap-2">
            <label className="text-xs font-medium text-[#4A5A50]">Translation · {targetName}</label>
            <div className="flex items-center gap-2">
              {result && (
                <button
                  type="button"
                  onClick={speak}
                  className="puvio-pill puvio-pill-light text-xs puvio-focus"
                  aria-label="Speak translation"
                  data-testid="translate-speak-button"
                >
                  <Volume2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={copy}
                disabled={!result}
                className={`puvio-pill text-xs puvio-focus transition-opacity ${result ? 'puvio-pill-accent' : 'puvio-pill-light opacity-50'}`}
                data-testid="translate-copy-button"
                aria-label="Copy translation"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <div className="mt-2 text-base leading-relaxed whitespace-pre-wrap min-h-[64px]" data-testid="translate-output">
            {result || (loading ? <span className="text-[#4A5A50] italic">Translating…</span> : <span className="text-[#4A5A50]">Your translation will appear here.</span>)}
          </div>
          {result && (
            <p className="mt-3 text-[10px] text-[#7A8A80]">
              Instant translation
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-medium text-[#4A5A50] px-1 mb-2">Quick phrases</p>
          <div className="flex flex-wrap gap-2">
            {QUICK.map((p, i) => (
              <button key={p} type="button" onClick={() => { setText(p); run(p); }} className="puvio-pill puvio-pill-light text-xs puvio-focus" data-testid={`translate-quick-${i}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
